import React, { useState } from "react";

import { Box, Button, Typography, Paper } from "@mui/material";

import { makeStyles } from "@mui/styles";

import DTextField from "components/common/DTextField";

import LoginHeader from "pages/Login/components/LoginHeader";
import LoginCard from "pages/Login/components/LoginCard";
import SideCard from "pages/Login/components/SideCard";
import PrimaryButton from "components/common/DButton";
import GridContainer from "components/common/GridContainer";
import GridItem from "components/common/GridItem";

import useIsMobile from "utils/helpers/mediaQueryHelper";
import userService from "services/User/userService";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "config";

const styles = {
    root: {
        margin: "auto",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        flexDirection: "column",
        background: "#fff"
    }
};

const useStyles = makeStyles(styles);

export default function ForgotPassword() {
    const classes = useStyles();
    const isMobile = useIsMobile();
    const navigate = useNavigate();

    const [email, setEmail] = useState("");
    const [errors, setErrors] = useState([]);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const validateEmail = (value) => {
        const errors = [];

        if (!value.trim()) {
            errors.push("Email address is required.");
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            errors.push("Please enter a valid email address.");
        }

        return errors;
    };

    const handleChange = (event) => {
        const value = event.target.value;

        setEmail(value);

        if (errors.length > 0) {
            setErrors(validateEmail(value));
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const validationErrors = validateEmail(email);

        if (validationErrors.length > 0) {
            setErrors(validationErrors);
            return;
        }

        setErrors([]);

        try {
            setLoading(true);

            await userService.forgotPassword(email);

            // Always show the same generic success message
            setIsSubmitted(true);
        } catch (error) {
            console.error("Forgot password error:", error);

            // Depending on how the backend is designed,
            // you may want to still show the generic message here.
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box px={2} className={classes.root}>
            <LoginCard>
                <GridContainer spacing={1} style={{ flexWrap: "nowrap" }}>
                    {!isMobile && (
                        <GridItem>
                            <SideCard />
                        </GridItem>
                    )}

                    <GridItem sx={{ flex: 1 }}>
                        <LoginHeader />

                        {isSubmitted ? (
                            <Paper
                                elevation={0}
                                sx={{
                                    textAlign: "center",
                                    mt: 2
                                }}
                            >
                                <Typography variant="h6" gutterBottom>
                                    Check Your Email
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    If an account exists with the provided email
                                    address, password reset instructions will be
                                    sent shortly.
                                </Typography>
                                <Box sx={{ mt: 3 }}>
                                    <PrimaryButton
                                        fullWidth
                                        onClick={() => navigate(ROUTES.LOGIN)}
                                    >
                                        Back to Login
                                    </PrimaryButton>
                                </Box>
                            </Paper>
                        ) : (
                            <Box
                                component="form"
                                onSubmit={handleSubmit}
                                noValidate
                            >
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    mb={3}
                                >
                                    Enter your registered email address to
                                    receive password reset instructions.
                                </Typography>

                                <DTextField
                                    fullWidth
                                    required
                                    label="Email Address"
                                    name="email"
                                    autoComplete="email"
                                    autoFocus
                                    value={email}
                                    onChange={handleChange}
                                    error={errors.length > 0}
                                    helperText={
                                        errors.length > 0 ? errors[0] : ""
                                    }
                                />

                                <Box sx={{ mt: 3 }}>
                                    <GridContainer noWrap>
                                        <GridItem xs>
                                            <PrimaryButton
                                                type="submit"
                                                fullWidth
                                                variant="contained"
                                                loading={loading}
                                            >
                                                Send Reset Link
                                            </PrimaryButton>
                                        </GridItem>
                                        <GridItem xs>
                                            <PrimaryButton
                                                fullWidth
                                                onClick={() =>
                                                    navigate(ROUTES.LOGIN)
                                                }
                                            >
                                                Back to Login
                                            </PrimaryButton>
                                        </GridItem>
                                    </GridContainer>
                                </Box>
                            </Box>
                        )}
                    </GridItem>
                </GridContainer>
            </LoginCard>
        </Box>
    );
}
